import React, { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { TooltipComponent, LegendComponent } from "echarts/components";
import { PieChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import { LabelLayout } from "echarts/features"; // ✅ needed for label lines
import {
  fixedPercentage,
  formatNumberCommaSeparated,
  truncateNumber,
} from "../../utils/common";
import { Box, Typography } from "@mui/material";

echarts.use([TooltipComponent, LegendComponent, PieChart, CanvasRenderer, LabelLayout]);

const EchartDoughnutChart = ({
  labels,
  dataPoints,
  colors,
  legendPosition = "top",
  customWidth,
  customHeight,
  hide,
  mt,
  /** When set, used for slice values and percentages (e.g. scientific for tiny numbers) */
  formatDisplayValue,
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !labels?.length || !dataPoints?.length || !colors?.length) {
      return;
    }

    const chart = echarts.init(chartRef.current);
    const total = dataPoints.reduce((a, v) => Number(a) + Number(v), 0);
    const SMALL_PCT = 6;
    const fmt =
      typeof formatDisplayValue === "function"
        ? formatDisplayValue
        : (v) => formatNumberCommaSeparated(truncateNumber(v));
    const fmtPct = (v) =>
      typeof formatDisplayValue === "function"
        ? formatDisplayValue(v, 2)
        : truncateNumber(v, 2);

    // build data with per-slice config
    const seriesData = labels.map((name, i) => {
      const value = Number(dataPoints[i] ?? 0);
      const pct = total ? (value / total) * 100 : 0;
      const isSmall = pct < SMALL_PCT;

      return {
        value,
        name,
        itemStyle: { color: colors[i] },
        label: {
          show: true,
          position: isSmall ? "outside" : "inside",
          color: isSmall ? "#000" : "#fff",
          fontSize: 10,
          fontWeight: "bold",
          formatter: (p) => {
            const per = fixedPercentage((Number(p.value) / total) * 100);
            return `${fmtPct(per)}%`;
          },
        },
        labelLine: isSmall
          ? {
              show: true,
              length: 8,
              length2: 6,
              smooth: true,
              lineStyle: { width: 1 },
            }
          : { show: false },
      };
    });

    const hasSmall = seriesData.some((s) => s.label?.position === "outside");
    const outerRadius = hasSmall ? "78%" : "90%";

    const option = {
      tooltip: {
        trigger: "item",
        formatter: (p) => {
          const percentage = fixedPercentage((Number(p.value) / total) * 100);
          return `${p.name}: ${fmt(p.value)} (${fmtPct(percentage)}%)`;
        },
      },
      series: [
        {
          type: "pie",
          radius: ["40%", outerRadius],
          center: ["50%", "50%"],
          avoidLabelOverlap: true,
          minShowLabelAngle: 0,
          padAngle: 3,
          itemStyle: { borderRadius: 4 },
          labelLine: { show: true },
          data: seriesData,
        },
      ],
    };

    chart.setOption(option);
    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      chart.dispose();
    };
  }, [labels, dataPoints, colors, legendPosition, formatDisplayValue]);

  return (
    <Box>
      {labels?.length > 0 && dataPoints?.length > 0 ? (
        <div
          ref={chartRef}
          style={{
            width: customWidth || "100%",
            height: customHeight || "100%",
            aspectRatio: "1",
            padding: "0px",
          }}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: customHeight || "100%",
            width: "100%",
            mt: mt,
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontFamily: "inter",
              color: "#d3d3d3",
              fontWeight: "bold",
            }}
          >
            {!hide && "No Data Found"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EchartDoughnutChart;
